import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, Filter, Calendar, Award, TrendingUp, Gift, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { AllCustomerShopping, AllProfile } from '../../services/api';
import toast from 'react-hot-toast';

const RewardsHistory = () => {
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [rewardsHistory, setRewardsHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchRewardsHistory();
  }, [currentUser]);

  const fetchRewardsHistory = async () => {
    if (!currentUser) return;
    
    try {
      const shoppingData = await AllCustomerShopping();
      
      if (shoppingData && shoppingData.length > 0) {
        // Filter data for current user and transform to rewards format
        const userTransactions = shoppingData
          .filter(item => 
            item.customerEmail === currentUser.email || 
            item.customerId === currentUser.uid
          )
          .map(item => ({
            id: item.id,
            type: item.pointsEarned > 0 ? 'earned' : 'purchase',
            points: item.pointsEarned || 0,
            description: `Purchase at ${item.shopName || item.businessName || 'Shop'}`,
            shop: item.shopName || item.businessName || 'Unknown Shop',
            date: item.date || item.createdAt || new Date().toISOString(),
            amount: `₹${parseFloat(item.amount || 0).toLocaleString()}`,
            category: item.category || 'general',
            transactionId: item.transactionId || item.id
          }));

        // Add some mock redemption and bonus entries for demonstration
        const mockRedemptions = [
          {
            id: 'bonus_1',
            type: 'bonus',
            points: 100,
            description: 'Welcome Bonus',
            shop: 'System',
            date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            amount: '-',
            category: 'bonus'
          },
          {
            id: 'redeem_1',
            type: 'redeemed',
            points: -50,
            description: 'Discount Applied',
            shop: 'Coffee Corner',
            date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
            amount: '₹50',
            category: 'redemption'
          }
        ];

        const allRewards = [...userTransactions, ...mockRedemptions];
        setRewardsHistory(allRewards);
      }
    } catch (error) {
      console.error('Error fetching rewards history:', error);
      toast.error('Failed to load rewards history');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/customer/dashboard');
  };

  // Filter and sort function
  const getFilteredAndSortedRewards = () => {
    let filtered = rewardsHistory.filter(reward => {
      const matchesSearch = reward.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           reward.shop.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterType === 'all' || reward.type === filterType;
      
      // Date range filter
      let matchesDate = true;
      if (dateRange !== 'all') {
        const rewardDate = new Date(reward.date);
        const now = new Date();
        
        switch (dateRange) {
          case 'today':
            matchesDate = rewardDate.toDateString() === now.toDateString();
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDate = rewardDate >= weekAgo;
            break;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            matchesDate = rewardDate >= monthAgo;
            break;
          case 'year':
            const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            matchesDate = rewardDate >= yearAgo;
            break;
        }
      }
      
      return matchesSearch && matchesFilter && matchesDate;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'points':
          aValue = Math.abs(a.points);
          bValue = Math.abs(b.points);
          break;
        case 'shop':
          aValue = a.shop.toLowerCase();
          bValue = b.shop.toLowerCase();
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const filteredRewards = getFilteredAndSortedRewards();

  // Calculate totals
  const totalEarned = rewardsHistory.filter(r => r.points > 0).reduce((sum, r) => sum + r.points, 0);
  const totalRedeemed = Math.abs(rewardsHistory.filter(r => r.points < 0).reduce((sum, r) => sum + r.points, 0));
  const currentBalance = totalEarned - totalRedeemed;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading rewards history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleGoBack}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Rewards History</h1>
                <p className="text-gray-600">Track your points earned and redeemed</p>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium">Total Earned</p>
                  <p className="text-2xl font-bold text-green-900">{totalEarned}</p>
                </div>
                <Award className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-600 text-sm font-medium">Total Redeemed</p>
                  <p className="text-2xl font-bold text-red-900">{totalRedeemed}</p>
                </div>
                <Gift className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-medium">Current Balance</p>
                  <p className="text-2xl font-bold text-blue-900">{currentBalance}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by description or shop..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="lg:w-48">
              <div className="relative">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >
                  <option value="all">All Types</option>
                  <option value="earned">Earned</option>
                  <option value="redeemed">Redeemed</option>
                  <option value="bonus">Bonus</option>
                  <option value="purchase">Purchase</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            <div className="lg:w-48">
              <div className="relative">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last Week</option>
                  <option value="month">Last Month</option>
                  <option value="year">Last Year</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            <div className="lg:w-48">
              <div className="relative">
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [sort, order] = e.target.value.split('-');
                    setSortBy(sort);
                    setSortOrder(order);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >
                  <option value="date-desc">Newest First</option>
                  <option value="date-asc">Oldest First</option>
                  <option value="points-desc">Highest Points</option>
                  <option value="points-asc">Lowest Points</option>
                  <option value="shop-asc">Shop A-Z</option>
                  <option value="shop-desc">Shop Z-A</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Rewards Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Date</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Type</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Description</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Shop</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Points</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredRewards.map((reward) => (
                  <tr key={reward.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-6 text-gray-500">{new Date(reward.date).toLocaleDateString()}</td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        reward.type === 'earned' ? 'bg-green-100 text-green-800' :
                        reward.type === 'redeemed' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {reward.type.charAt(0).toUpperCase() + reward.type.slice(1)}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-900">{reward.description}</td>
                    <td className="py-4 px-6 text-gray-700">{reward.shop}</td>
                    <td className="py-4 px-6">
                      <span className={`font-semibold ${
                        reward.points > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {reward.points > 0 ? '+' : ''}{reward.points}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-700">{reward.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* No results message */}
          {filteredRewards.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No rewards found</h3>
              <p className="text-gray-600">Try adjusting your search criteria or filters.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardsHistory;